import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

/**
 * Standard page header (Design System Phase 3 §3).
 *
 * Every list/detail page renders one of these at the top: a title (text-2xl
 * semibold), an optional description, an optional right-aligned actions slot,
 * and an optional back link shown above the title on detail pages.
 *
 * It standardises only the top of the page — filters / tables / forms flow
 * naturally below it.
 */
export function PageHeader({
  title,
  description,
  actions,
  backHref,
}: {
  title: string;
  description?: string;
  /** Right-aligned action buttons (rendered as-is). */
  actions?: ReactNode;
  /** When set, a "← Back" ghost link renders above the title. */
  backHref?: string;
}) {
  return (
    <div className="mb-6">
      {backHref && (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 h-8 px-2 text-slate-500 hover:text-slate-900"
        >
          <Link to={backHref}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
